import groupBy from 'lodash.groupby';

import {
  HotelAvailabilityStatusType,
  RoomInfoViewModel,
  RoomVariationViewModel,
  RoomViewModel,
} from '@api/v3';

import { cloudineryImage } from './utils/features';
import { RoomsVariationBarItem } from './components/rooms-variation-bar/Item/props';
import { GalleryCategory } from './HotelDetail';

// =====================================================
// Get the lowest variation price in a rooms variations
// =====================================================
export const getLowestVariation = (roomsDetail: RoomInfoViewModel[]) => {
  const roomVariations: RoomVariationViewModel[] = [];

  if (!roomsDetail) return;

  roomsDetail?.forEach((room) =>
    room.informations?.forEach((roomInfo) =>
      roomVariations.push(...roomInfo.variations)
    )
  );

  const lowestPrice =
    Math.min(
      ...roomVariations.map((item) => item.totalDiscountedSellingPrice)
    ) ?? 0;

  if (!lowestPrice) return roomVariations?.[0];

  const lowestPriceItem = roomVariations?.find(
    (item) => item.totalDiscountedSellingPrice === lowestPrice
  );

  return lowestPriceItem;
};

// =====================================================
// Group Images By Category
// =====================================================
export const groupImagesByCategory = <
  T extends { url?: string; hotelImageCategoryName?: string }
>(
  images: T[],
  groupingKey?: string,
  hotelName?: string,
  rooms?: RoomViewModel[]
) => {
  const roomsCategory = getRoomsGalleryCategory(rooms ?? []);

  const imagesForGallery = images?.map((p, i) => ({
    ...p,
    original:
      cloudineryImage(p.url, '840', null) ?? '/static/images/setur-static.jpg',
    originalAlt: `${hotelName || ''} ${p?.hotelImageCategoryName || ''} ${
      i + 1
    }`,
    thumbnailAlt: `${hotelName || ''} ${p.hotelImageCategoryName || ''} küçük ${
      i + 1
    }`,
    thumbnail:
      cloudineryImage(p.url, '137', null) ?? '/static/images/setur-static.jpg',
  }));
  const imageCategories = groupingKey
    ? groupBy(imagesForGallery, groupingKey)
    : { 'Oda Fotografları': imagesForGallery };
  return {
    ...imageCategories,
    all: [...imagesForGallery, ...roomsCategory],
  } as { [key: string]: typeof imagesForGallery };
};

// =====================================================
// Get the room with the status of available
// =====================================================
export const getAvailableRoom = (roomsDetail: RoomInfoViewModel[]): boolean => {
  if (!roomsDetail) return false;

  for (const item of roomsDetail) {
    const availableRoom = item?.informations.find(
      (roomInfo) =>
        roomInfo.availabilityStatus === HotelAvailabilityStatusType.AVAILABLE
    );

    if (availableRoom) return true;
  }
  return false;
};

// =====================================================
// Get the room variations base on available option
// =====================================================
export const getOnlyAvailableRoomVariation = (
  items: RoomsVariationBarItem[],
  isAvailable: boolean
) => {
  return items?.filter((item) => item.isAvailable === isAvailable) ?? [];
};

// =====================================================
// Get the room variations base on available option
// =====================================================
export const sortRoomVariationItems = (items: RoomsVariationBarItem[]) => {
  return items?.sort((a, b) => a.price - b.price) ?? [];
};

// =====================================================
// Get the rooms gallery category
// =====================================================
export const getRoomsGalleryCategory = (rooms: RoomViewModel[]) => {
  const roomsCategory: GalleryCategory[] = [];

  rooms?.forEach((room) =>
    room.images.forEach((roomImages) =>
      roomsCategory.push({
        url: roomImages?.url ?? '/static/images/setur-static.jpg',
        hotelImageCategoryName: 'Odalar',
        original:
          cloudineryImage(roomImages?.url, '420', '250') ??
          '/static/images/setur-static.jpg',
        originalAlt: room?.name ?? 'Setur Görseli Hazırlanmakta',
        thumbnailAlt: room?.name ?? 'Setur Görseli Hazırlanmakta',
        thumbnail:
          cloudineryImage(roomImages?.url, '137', '65') ??
          '/static/images/setur-static.jpg',
        description:
          room.name && room.typeName ? room.name + ' ' + room.typeName : '',
      })
    )
  );

  return roomsCategory;
};
